/**
 * The voice dock's three preferences, and the one storage contract behind them.
 *
 * Two of these already exist in `VoiceCommandModal.vue` as a pair of hand-rolled
 * readers (`readVoiceRepliesPreference`, `readFollowPreference`) and a pair of
 * hand-rolled writers, each with its own try/catch and its own idea of what a
 * missing value means. The parsing rules below are copied from those functions
 * rather than tidied, because the keys are already in real users' localStorage:
 * `sa-voice-tts` defaults ON and only the exact string `"false"` turns it off,
 * while `sa-voice-follow` defaults OFF and only the exact string `"true"` turns
 * it on. Making them symmetrical would silently flip one of them for everyone
 * who has ever used the modal.
 *
 * Nothing here touches `window`. The storage object is an argument so a node
 * test can hand it a plain in-memory map, and so a caller in a private-mode
 * browser (where reading `window.localStorage` can itself throw) decides for
 * itself what to pass. `null` is a legitimate argument and yields the defaults.
 *
 * Every function returns a fresh object. `DEFAULTS` is frozen precisely so that
 * a caller who mutates what it got back cannot move the defaults out from under
 * the next caller.
 *
 * THERE WAS A FOURTH, `fastMoves`, behind the gear's "Fast moves (experiment)"
 * row. Apply-then-undo was dropped for good (Andrew, 2026-09-18): confirm-first
 * stays for every mutation, so the row and the preference are gone. The storage
 * key `sa-voice-fast-moves` is deliberately NOT read, NOT written and NOT
 * cleaned up. Anyone who ever opened that row still has the string sitting in
 * localStorage, and `readPrefs` simply does not look at it — an unknown key is
 * not an error, and a migration that deleted it would be a write on every dock
 * open to tidy something nobody can see.
 */

export const DEFAULTS = Object.freeze({
  replies: true,
  micMode: "hold",
  follow: false,
});

export const KEYS = Object.freeze({
  replies: "sa-voice-tts",
  follow: "sa-voice-follow",
  micMode: "sa-voice-mic-mode",
});

const MIC_MODES = ["hold", "tap", "free"];

/** The primary pointer decides; hover capability does not override touch. */
export function defaultMicMode({ coarsePointer }) {
  return coarsePointer ? "hold" : "tap";
}

/** A boolean, or the default. A string, a number and `undefined` are all "not a boolean". */
function booleanOr(value, fallback) {
  return typeof value === "boolean" ? value : fallback;
}

/** A write that must never take the dock down with it. */
function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    /* Persistence is optional: a full quota or a blocked store loses the
       preference for next time, it does not fail the change the user just made. */
  }
}

/**
 * In-memory values (not storage strings) coerced field by field.
 *
 * Deliberately strict about booleans: `replies: "no"` is not `false`, it is
 * garbage, so it falls back to the default `true`. A truthiness test here would
 * read `"false"` as ON and `0` as OFF, which is how a preference module starts
 * disagreeing with the storage layer underneath it.
 */
export function normalisePrefs(input) {
  const source = input && typeof input === "object" ? input : {};
  return {
    replies: booleanOr(source.replies, DEFAULTS.replies),
    micMode: MIC_MODES.includes(source.micMode)
      ? source.micMode
      : DEFAULTS.micMode,
    follow: booleanOr(source.follow, DEFAULTS.follow),
  };
}

/**
 * `storage` is anything with `getItem(key)`, or `null`.
 *
 * A store that throws on read is treated as no store at all: the whole read is
 * inside one try/catch, so a browser that refuses the first key does not hand
 * back three parsed preferences and one silent default.
 */
export function readPrefs(storage, { micMode: fallbackMode } = {}) {
  const defaults = normalisePrefs({ micMode: fallbackMode });
  if (!storage || typeof storage.getItem !== "function") {
    return { ...defaults };
  }

  try {
    const replies = storage.getItem(KEYS.replies);
    const follow = storage.getItem(KEYS.follow);
    const micMode = storage.getItem(KEYS.micMode);

    return {
      // Absent or anything but the literal "false" means on. Same test as
      // `readVoiceRepliesPreference` in VoiceCommandModal.vue.
      replies: replies !== "false",
      micMode: micMode == null
        ? defaults.micMode
        : MIC_MODES.includes(micMode) ? micMode : DEFAULTS.micMode,
      // Only the literal "true" means on. Same test as `readFollowPreference`.
      follow: follow === "true",
    };
  } catch {
    return { ...defaults };
  }
}

/**
 * Writes ALL three keys, so the stored set can never be half a generation behind
 * the object the dock is holding, and returns the normalised prefs whether or
 * not the write landed. Callers use the return value: it is what actually took
 * effect, which is not necessarily what was passed in.
 */
export function writePrefs(storage, prefs) {
  const next = normalisePrefs(prefs);

  if (storage && typeof storage.setItem === "function") {
    // Per key, not one block: a store that rejects one write should still take
    // the other two rather than dropping everything after the first failure.
    safeSet(storage, KEYS.replies, String(next.replies));
    safeSet(storage, KEYS.follow, String(next.follow));
    safeSet(storage, KEYS.micMode, next.micMode);
  }

  return next;
}
